import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import {
	SovosButton,
	SovosDialog,
	SovosDialogActions,
	SovosDialogContent,
	SovosDialogTitle,
	SovosLinearProgress,
	SovosPageContentGeneric,
	SovosPageLayout,
	SovosSnackbar,
	SovosStep,
	SovosStepButton,
	SovosStepper,
} from 'mosaic-react';
import React, { useCallback, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import type { fetchLocationMappings } from 'src/api/locationMappings';
import {
	fetchLocationMapping,
	fetchMappingFields,
	saveLocationMapping,
	updateLocationMapping,
} from 'src/api/locationMappings';
import useBlocker from 'src/hooks/useBlocker';
import type { LocationMappingInput } from 'src/interfaces/LocationMapping';
import type { RouteExitConfirmationDialogState } from 'src/interfaces/RouteExitConfirmationDialogState';

import type { LocationMappingEditorFormState } from './LocationMappingEditorTypes';
import StepFileDetails from './steps/StepFileDetails';
import StepMap from './steps/StepMap';
import StepUpload from './steps/StepUpload';

const stepsOrder = ['File details', 'Upload', 'Map'] as const;

const LocationsMappingEditor: React.FC = () => {
	const queryClient = useQueryClient();

	const { locationMappingID } = useParams();
	const navigate = useNavigate();

	const [errorSnackbar, setErrorSnackbar] = useState(false);

	const [exitConfirmationDialogContinue, setExitConfirmationDialogContinue] =
		useState<RouteExitConfirmationDialogState>({
			isOpen: false,
			continue: null,
		});

	const fetchMappingFieldsQuery = useQuery(
		['MappingFields'],
		fetchMappingFields
	);

	const fetchLocMapByIdQuery = useQuery(
		['LocationMapping', locationMappingID],
		() => fetchLocationMapping(Number(locationMappingID)),
		{
			enabled: Boolean(locationMappingID),

			onSuccess: (data) => {
				if (!formState || !hasChanges) {
					setFormState(data);
				}
			},
			onError: () => {
				navigate('/location-mappings');
			},
		}
	);

	const saveLocMapMutationMutation = useMutation(
		() => {
			const locationMapping = {
				...formState,
				mappedFields: formState?.mappedFields?.map((mappedField) => ({
					...mappedField,
					isRequired: Boolean(
						fetchMappingFieldsQuery.data?.find(
							(el) => el.fieldName === mappedField.targetField
						)?.required
					),
				})),
			} as LocationMappingInput;

			const id = Number(locationMappingID);

			if (id && fetchLocMapByIdQuery.data) {
				return updateLocationMapping(id, locationMapping);
			}

			return saveLocationMapping(locationMapping);
		},
		{
			onError: () => setErrorSnackbar(true),
			onSuccess: (data) => {
				// updates cached location mappings list with updated data after mutation success
				queryClient.setQueriesData(
					{ queryKey: ['LocationMappings'] },
					(cachedData?: Awaited<ReturnType<typeof fetchLocationMappings>>) => {
						if (cachedData) {
							const items = [...cachedData.items];

							const existingItemIndex = items.findIndex(
								(item) => item.id === data.id
							);

							if (existingItemIndex >= 0) {
								items[existingItemIndex] = data;
							} else {
								items.unshift(data);
							}

							return {
								...cachedData,
								items,
							};
						}

						return cachedData;
					}
				);

				// updates cached location mapping with updated data after mutation success
				queryClient.setQueryData(['LocationMapping', data.id.toString()], data);

				// refetch location mappings list after mutation
				queryClient.invalidateQueries(['LocationMappings']);
			},
		}
	);

	const [currentStep, setCurrentStep] = useState(0);
	const [formState, setFormState] = useState<LocationMappingEditorFormState>(
		fetchLocMapByIdQuery.data || {}
	);

	const hasChanges = useMemo(() => {
		if (formState) {
			const savedState = fetchLocMapByIdQuery.data;

			return savedState
				? !isEqual(savedState, formState)
				: Object.keys(formState).length > 1;
		}
		return false;
	}, [fetchLocMapByIdQuery.data, formState]);

	useBlocker(
		(tx) =>
			setExitConfirmationDialogContinue({ isOpen: true, continue: tx.retry }),
		hasChanges &&
			!exitConfirmationDialogContinue.isOpen &&
			!saveLocMapMutationMutation.isSuccess
	);

	const handleOnContinue = useCallback(() => {
		setCurrentStep(currentStep + 1);
	}, [currentStep]);

	const steps = {
		'File details': (
			<StepFileDetails
				previousName={fetchLocMapByIdQuery.data?.name}
				fileName={formState?.name}
				setFileName={(name) => setFormState({ ...formState, name })}
				description={formState?.description}
				setFileDescription={(description) =>
					setFormState({ ...formState, description })
				}
				onContinue={handleOnContinue}
			/>
		),
		Upload: (
			<StepUpload
				inputFileName={formState?.inputFileName}
				setInputFileName={(inputFileName) =>
					setFormState((formState) => ({ ...formState, inputFileName }))
				}
				mappedFields={formState?.mappedFields}
				setMappedFields={(mappedFields) =>
					setFormState((formState) => ({ ...formState, mappedFields }))
				}
				onContinue={handleOnContinue}
			/>
		),
		Map: (
			<StepMap
				selectedMap={formState?.mappedFields}
				mappingFields={fetchMappingFieldsQuery.data}
				setSelectedMap={(mappedFields) =>
					setFormState({ ...formState, mappedFields })
				}
				onContinue={() => {
					saveLocMapMutationMutation.mutate();
				}}
				isSubmitting={saveLocMapMutationMutation.isLoading}
				isNewLocationMapping={!locationMappingID}
			/>
		),
	};

	return saveLocMapMutationMutation.isSuccess ? (
		<Navigate
			to='..'
			relative='path'
			state={{ savedLocationMapping: locationMappingID ? 'UPDATE' : 'CREATE' }}
		/>
	) : (
		<SovosPageLayout
			pageTitle={
				Boolean(locationMappingID)
					? 'Edit Location Mappings'
					: 'Create New Location Mapping'
			}
			pageCrumbs={[
				{
					text: 'Location Mappings',
					onClick: () => navigate('/location-mappings'),
				},
			]}
		>
			<SovosPageContentGeneric sx={{ paddingBottom: 3 }}>
				{!locationMappingID ||
				(!fetchLocMapByIdQuery.isLoading && formState) ? (
					<>
						<SovosStepper
							activeStep={currentStep}
							sx={{ px: 0, pt: 0, ml: -1, width: 1 / 3 }}
						>
							{stepsOrder.map((step, i) => (
								<SovosStep key={step}>
									<SovosStepButton onClick={() => setCurrentStep(i)}>
										{step}
									</SovosStepButton>
								</SovosStep>
							))}
						</SovosStepper>
						{steps[stepsOrder[currentStep]]}
					</>
				) : (
					<SovosLinearProgress />
				)}

				<SovosSnackbar
					open={errorSnackbar}
					onClose={() => setErrorSnackbar(false)}
					variant='error'
					message='An error occurred with your location mapping. Please try again.'
					timeout='short'
				/>

				<SovosDialog
					open={Boolean(exitConfirmationDialogContinue.isOpen)}
					onClose={() =>
						setExitConfirmationDialogContinue({ isOpen: false, continue: null })
					}
					width='small'
				>
					<SovosDialogTitle showCloseButton>
						{`Cancel ${Boolean(locationMappingID) ? 'edit' : 'create'} location
						mapping?`}
					</SovosDialogTitle>
					<SovosDialogContent>
						The location mapping information you’ve entered will be lost. Are
						you sure you want to cancel?
					</SovosDialogContent>
					<SovosDialogActions>
						<SovosButton
							variant='text'
							onClick={() =>
								setExitConfirmationDialogContinue({
									isOpen: false,
									continue: null,
								})
							}
						>
							Keep editing
						</SovosButton>
						<SovosButton
							color='error'
							onClick={() => {
								if (exitConfirmationDialogContinue.continue)
									exitConfirmationDialogContinue.continue();
							}}
						>
							Exit without saving
						</SovosButton>
					</SovosDialogActions>
				</SovosDialog>
			</SovosPageContentGeneric>
		</SovosPageLayout>
	);
};

export default LocationsMappingEditor;
